import React, { useState, useMemo } from "react";
import { MdViewList, MdGridView, MdAltRoute } from "react-icons/md";

import { useFilesContext } from "../../context/domain/files/FilesContext";

import { getFolderInfo, getFileLink } from "../../services/api/files";

import PhotoExplorer from "../../features/files/drive/photoExplorer";
import Explorer from "../../features/files/drive/Explorer";

const Drive = () => {
  document.title = "R&S Shared Drive";
  const {
    folderData,
    setFolderData,
    isGridView,
    setIsGridView,
    isDynamicView,
    setIsDynamicView,
  } = useFilesContext();

  const structure = useMemo(
    () => ({
      level0: [
        {
          title: "Folders",
          type: "explorer",
          urlAppend: "",
          folders: "projects",
          files: "none",
          dynamicDisplay: "list",
          comments: false,
        },
      ],
      level1: [
        {
          type: "explorer",
          title: "Plans",
          urlAppend: "/01-Pre-Bid",
          folders: "none",
          files: "documents",
          dynamicDisplay: "grid",
          comments: false,
        },
        {
          type: "photoExplorer",
          title: "Photos",
          urlAppend: "/01-Pre-Bid",
          folders: "none",
          files: "documents",
          dynamicDisplay: "grid",
          comments: true,
        },
      ],
      level2: [
        {
          type: "explorer",
          title: "Name",
          urlAppend: "/01-Pre-Bid",
          folders: "none",
          files: "documents",
        },
      ],
    }),
    []
  );

  const [currentPath, setCurrentPath] = useState("Shared/Projects");
  const [searchQuery, setSearchQuery] = useState("");

  const isHome = currentPath === "Shared/Projects";
  const level = "level" + (currentPath.split("/").length - 2);

  const handleFolderClick = async (folderName) => {
    const newPath = folderName.startsWith("/") // We do not want any lading slashes
      ? folderName.substring(1)
      : folderName;
    const newLevel = "level" + (newPath.split("/").length - 2);
    try {
      if (structure[newLevel] && structure[newLevel][0]) {
        const folderInfo = await getFolderInfo(
          newPath + structure[newLevel][0]["urlAppend"]
        );
        setFolderData(folderInfo);
        setCurrentPath(newPath);
      } else {
        console.error(`Invalid structure level: ${newLevel}`);
      }
    } catch (error) {
      console.error("Failed to fetch folder info:", error);
    }
  };

  const handleBackClick = async () => {
    if (isHome) return;
    const newPath = currentPath.substring(0, currentPath.lastIndexOf("/"));
    try {
      const folderInfo = await getFolderInfo(newPath);
      setFolderData(folderInfo);
      setCurrentPath(newPath);
    } catch (error) {
      console.error("Failed to fetch folder info:", error);
    }
  };

  const handleFileClick = async (filePath) => {
    try {
      const data = await getFileLink(filePath);
      if (data["links"][0].url) {
        window.open(data.links[0].url, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      console.error("Error fetching file link:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 font-inter">
      <div className="p-4">
        <div className="flex flex-col md:flex-row justify-between items-center max-w-[1024px] mb-4 mx-auto space-y-4 md:space-y-0">
          <h1 className="text-2xl font-bold text-center md:text-left pl-0 md:pl-2">
            R&S Shared Drive
          </h1>
          <div className="flex items-center space-x-4">
            <input
              type="text"
              placeholder="Search filename..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-1 w-full md:w-64 h-[32px]"
            />
            <div className="flex items-center border border-gray-300 rounded-full overflow-hidden h-[32px] min-w-[96px]">
              <div
                className={`p-2 ${
                  !isGridView && !isDynamicView ? "bg-blue-100" : "bg-white"
                } flex items-center`}
                role="button"
                onClick={() => {
                  setIsGridView(false);
                  setIsDynamicView(false);
                }}
              >
                <MdViewList className="text-gray-700" />
              </div>
              <div
                className={`p-2 ${
                  isGridView ? "bg-blue-100" : "bg-white"
                } flex items-center`}
                role="button"
                onClick={() => {
                  setIsGridView(true);
                  setIsDynamicView(false);
                }}
              >
                <MdGridView className="text-gray-700" />
              </div>
              <div
                className={`p-2 ${
                  isDynamicView ? "bg-blue-100" : "bg-white"
                } flex items-center`}
                role="button"
                onClick={() => {
                  setIsDynamicView(true);
                  setIsGridView(false);
                }}
              >
                <MdAltRoute className="text-gray-700" />
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-[1024px] m-auto">
          <div className="flex flex-row border-b font-semibold text-gray-600 rounded-l-lg bg-gray-200 shadow-md">
            <div
              className={`px-2 rounded-l-lg ${
                !isHome ? "hover:bg-gray-300" : "text-gray-400"
              }`}
            >
              <button
                className={`text-center p-2 min-w-[75px]`}
                onClick={handleBackClick}
                disabled={isHome}
              >
                {!isHome ? "← Back" : "Home"}
              </button>
            </div>
            <div className="text-left p-2 pl-4 rounded-r-l truncate col-span-3 text-gray-400 bg-white w-full">
              {currentPath.split("/").map((part, index, arr) => {
                const truncatedPart =
                  part.length > 10 ? `${part.slice(0, 10)}...` : part;
                const pathToHere = arr.slice(0, index + 1).join("/");
                return (
                  <span
                    key={index}
                    className={`${
                      arr.length > 2 &&
                      index !== arr.length - 1 &&
                      "cursor-pointer"
                    } ${
                      index < arr.length - 1 ? "text-blue-500" : "text-gray-400"
                    } ${index !== arr.length - 1 && "truncate"}`}
                    onClick={() => handleFolderClick(pathToHere)}
                  >
                    {index > 0 &&
                      (index !== arr.length - 1 ? truncatedPart : part)}
                    {index < arr.length - 1 && index !== 0 && " / "}
                  </span>
                );
              })}
            </div>
          </div>
          {structure[level].map((layout, index) =>
            layout.type === "explorer" ? (
              <Explorer
                key={index}
                title={layout["title"]}
                folderData={folderData}
                structure={layout}
                isGridView={
                  isDynamicView && layout["dynamicDisplay"] === "grid"
                    ? true
                    : isGridView
                }
                searchQuery={searchQuery}
                handleFolderClick={handleFolderClick}
                handleFileClick={handleFileClick}
                comments={layout["comments"]}
              />
            ) : layout.type === "photoExplorer" ? (
              <PhotoExplorer
                key={index}
                title={layout["title"]}
                path={currentPath}
                isGridView={
                  isDynamicView && layout["dynamicDisplay"] === "grid"
                    ? true
                    : isGridView
                }
                searchQuery={searchQuery}
                // Handle folder click is handled in the photoExplorer component due
                // to needing separate navigation states and logic
                handleFileClick={handleFileClick}
                comments={layout["comments"]}
              />
            ) : null
          )}
        </div>
      </div>
    </div>
  );
};
export default Drive;
